import { profileEquipItem, profileEquipMerc, stateRemoveItem } from 'logic/stateReducers';
import createStore from './factory';

const identity = state => state;

const defaultQuestTable = {
  denofevil: true,
  radamentslair: true,
  thegoldenbird: true,
  lamessenstome: true,
  thefallenangel: true,
  prisonofice: true,
};

// This is not an actual store
export class ProfileStore {
  static actions = {
    setName(global, state, name) {
      if (name === state.name) return state;
      return {...state, name};
    },

    setSkill(global, state, id, value) {
      const prev = state.skills[id];
      if (typeof value === "function") value = value(prev, state, global);
      if (value === prev) return state;
      state = {...state};
      state.skills = {...state.skills};
      if (value) {
        state.skills[id] = value;
      } else {
        delete state.skills[id];
      }
      return state;
    },

    setStat(global, state, id, value) {
      const prev = state.stats[id];
      if (typeof value === "function") value = value(prev, state, global);
      if (value === prev) return state;
      state = {...state};
      state.stats = {...state.stats};
      if (value) {
        state.stats[id] = value;
      } else {
        delete state.stats[id];
      }
      return state;
    },

    setItem(global, state, id, value) {
      return profileEquipItem(state, id, value, global);
    },

    setMercItem(global, state, id, value) {
      return profileEquipMerc(state, id, value, global);
    },

    setStorage(global, state, type, value) {
      const prev = state[type];
      if (typeof value === "function") value = value(prev, state, global);
      if (value === prev) return state;
      return {...state, [type]: value};
    },

    setActive(global, state, id, value) {
      const prev = state.active[id];
      if (typeof value === "function") value = value(prev, state, global);
      if (value === prev) return state;
      return {...state, active: {...state.active, [id]: value}};
    },

    setProfile(global, state, value) {
      if (typeof value === "function") value = value(state, global);
      return value;
    },

    setWeaponSet(global, state, value) {
      const prev = state.weaponSet;
      if (typeof value === "function") value = value(prev, state, global);
      if (value === prev) return state;
      return {...state, weaponSet: value};
    },
  }

  static initialState = {
    id: 1,
    name: "Set 1",
    skills: {},
    stats: {
      str: 0,
      dex: 0,
      int: 0,
      vit: 0,
    },
    inventory: [],
    cube: [],
    class: "sor",
    level: 99,
    items: {},
    active: {},
    merc: null,
    mercLevel: 98,
    mercItems: {},
    difficulty: 2,
    weaponSet: 0,
    quests: [defaultQuestTable, defaultQuestTable, defaultQuestTable],
    // skillProgression: [],
    // skillPos: number
    // ironGolem: id
    // monster: {}
  }
}

export default class RootStore extends createStore({
  addProfile(state, profile) {
    if (typeof profile === "function") profile = profile(state);
    return {...state, profiles: [...state.profiles, profile], activeProfile: state.profiles.length};
  },

  setProfile(state, value) {
    const prev = state.profiles[state.activeProfile];
    if (typeof value === "function") value = value(prev, state);
    if (value === prev) return state;
    state = {...state, profiles: state.profiles.slice()};
    state.profiles[state.activeProfile] = value;
    return state;
  },

  setActiveProfile(state, id) {
    const index = state.profiles.findIndex(profile => profile.id === id);
    if (index < 0 || index === state.activeProfile) return state;
    return {...state, activeProfile: index};
  },

  renameProfile(state, id, name) {
    const index = state.profiles.findIndex(profile => profile.id === id);
    if (index < 0 || state.profiles[index].name === name) return state;
    state = {...state};
    state.profiles = state.profiles.slice();
    state.profiles[index] = {...state.profiles[index], name};
    return state;
  },

  moveProfile(state, from, to) {
    if (from === to) return state;
    state = {...state, profiles: state.profiles.slice()};
    const activeId = state.profiles[state.activeProfile].id;
    const src = state.profiles.splice(from, 1);
    state.profiles.splice(to, 0, ...src);
    state.activeProfile = state.profiles.findIndex(profile => profile.id === activeId);
    return state;
  },

  setBuffs(state, value) {
    const prev = state.buffs;
    if (typeof value === "function") value = value(prev, state);
    if (value === prev) return state;
    return {...state, buffs: value};
  },

  setSummons(state, value) {
    const prev = state.summons;
    if (typeof value === "function") value = value(prev, state);
    if (value === prev) return state;
    return {...state, summons: value};
  },

  setState(state, value) {
    if (typeof value === "function") value = value(state);
    value = {...value};
    if (!value.profiles[value.activeProfile]) {
      value.activeProfile = 0;
    }
    return value;
  },

  setItem(state, id, value) {
    const prev = state.items[id];
    if (typeof value === "function") value = value(prev, state);
    if (value === prev) return state;
    if (!value) return stateRemoveItem(state, id);
    state = {...state};
    state.items = {...state.items};
    state.items[id] = value;
    return state;
  },

  setName(state, name) {
    if (name === state.name) return state;
    return {...state, name};
  },
}, {
  id: 0,
  profiles: [ProfileStore.initialState],
  items: {},
  activeProfile: 0,
  buffs: {},
  summons: {},
}) {
  static uniqueId = 1;
  static newId() {
    return ++RootStore.uniqueId;
  }

  constructor(initialState) {
    super(initialState);
    this.dispatch.profile = {};
    for (const action in ProfileStore.actions) {
      this.dispatch.profile[action] = (...args) => this.dispatch.setProfile((profile, state) => ProfileStore.actions[action](state, profile, ...args));
    }
  }

  getProfile() {
    const state = this.getState();
    return state.profiles[state.activeProfile];
  }

  setState(value) {
    return new Promise((resolve, reject) => {
      let unsubscribe;
      const handler = () => {
        unsubscribe();
        resolve();
      };
      unsubscribe = this.store.subscribe(handler);
      this.dispatch.setState(value);
    });
  }

  static useProfile(func, depends) {
    func = func || identity;
    return this.useSelector(state => {
      const profile = state.profiles[state.activeProfile];
      return func(profile);
    }, depends);
  }
  static useItem(id) {
    return this.useSelector(state => state.items[id]);
  }
}

RootStore.instance = new RootStore();
